import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Rocket, LayoutDashboard, Package, Upload, Calendar, Truck, Wallet,
  FileSpreadsheet, Flag, Bell, MapPin, MapPinned, Users, UserCircle2,
  Store, Activity, TrendingUp, ChevronDown, ChevronRight, BookOpen, Search, Calculator
} from "lucide-react";

const ICON_MAP = {
  "rocket": Rocket, "layout-dashboard": LayoutDashboard, "package": Package,
  "upload": Upload, "calendar": Calendar, "truck": Truck, "wallet": Wallet,
  "file-spreadsheet": FileSpreadsheet, "flag": Flag, "bell": Bell,
  "map-pin": MapPin, "map-pinned": MapPinned, "users": Users,
  "user-circle": UserCircle2, "store": Store, "activity": Activity,
  "trending-up": TrendingUp, "calculator": Calculator
};

const DocumentationPage = ({ user, onLogout, Layout }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchDocs();
  }, []);

  const fetchDocs = async () => {
    try {
      const response = await axios.get(`${API}/docs/`);
      setDocs(response.data);
      if (response.data.length > 0) {
        setActiveSection(response.data[0].id);
        // Expand first section by default
        setExpandedSections({ [response.data[0].id]: true });
      }
    } catch (error) {
      console.error("Failed to fetch documentation", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
    setActiveSection(id);
  };

  const renderContent = (text) => {
    return text.split('\n').map((line, i) => {
      // Convert markdown bold to HTML
      const htmlLine = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      // Bullet list items
      if (line.startsWith('- ')) {
        return <li key={i} className="ml-4 mb-1" dangerouslySetInnerHTML={{ __html: htmlLine.substring(2) }} />;
      }
      // Numbered list items
      if (line.match(/^\d+\.\s/)) {
        return <li key={i} className="ml-4 mb-1 list-decimal" dangerouslySetInnerHTML={{ __html: htmlLine.replace(/^\d+\.\s/, '') }} />;
      }
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className="mb-2" dangerouslySetInnerHTML={{ __html: htmlLine }} />;
    });
  };

  // Filter docs by search
  const filteredDocs = searchQuery
    ? docs.filter(doc =>
        doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.sections.some(s =>
          s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.content.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : docs;

  const activeDoc = filteredDocs.find(d => d.id === activeSection) || filteredDocs[0];

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex justify-center py-12">
          <div className="text-slate-600">Loading documentation...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-1">Documentation</h1>
            <p className="text-slate-600">Complete guide to using EdgeLMS</p>
          </div>
          <Badge className="bg-teal-100 text-teal-700 border-0 text-sm px-3 py-1">
            <BookOpen className="w-4 h-4 mr-1.5" />
            v1.0
          </Badge>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search documentation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            data-testid="docs-search"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <nav className="space-y-1 sticky top-4">
              {filteredDocs.map((doc) => {
                const Icon = ICON_MAP[doc.icon] || BookOpen;
                const isActive = activeSection === doc.id;
                return (
                  <button
                    key={doc.id}
                    onClick={() => { setActiveSection(doc.id); setExpandedSections(prev => ({ ...prev, [doc.id]: true })); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left text-sm transition-colors ${
                      isActive
                        ? "bg-teal-50 text-teal-700 font-medium"
                        : "text-slate-600 hover:bg-slate-100"
                    }`}
                    data-testid={`docs-nav-${doc.id}`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{doc.title}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="lg:col-span-3 space-y-4">
            {activeDoc ? (
              <>
                <div className="flex items-center gap-3 mb-2">
                  {(() => {
                    const Icon = ICON_MAP[activeDoc.icon] || BookOpen;
                    return <Icon className="w-6 h-6 text-teal-600" />;
                  })()}
                  <h2 className="text-2xl font-bold text-slate-800">{activeDoc.title}</h2>
                </div>

                {activeDoc.sections.map((section, idx) => (
                  <Card key={idx} className="border border-slate-200 shadow-sm" data-testid={`docs-section-${activeDoc.id}-${idx}`}>
                    <button
                      onClick={() => toggleSection(`${activeDoc.id}-${idx}`)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left"
                    >
                      <h3 className="text-base font-semibold text-slate-800">{section.title}</h3>
                      {expandedSections[`${activeDoc.id}-${idx}`] !== false ? (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      )}
                    </button>
                    {expandedSections[`${activeDoc.id}-${idx}`] !== false && (
                      <CardContent className="pt-0 px-5 pb-5">
                        <div className="text-sm text-slate-700 leading-relaxed">
                          {renderContent(section.content)}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </>
            ) : (
              <div className="text-center py-12 text-slate-500">
                {searchQuery ? "No results found for your search." : "Select a topic from the sidebar."}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DocumentationPage;
